/**
 * EmailComposeModal — Fenêtre de composition email universelle
 *
 * - Charge le modèle email (sujet + corps) depuis la DB
 * - L'aperçu est IDENTIQUE au mail envoyé : wrapper HTML complet avec logo agence
 * - Corps du message pré-rempli avec le texte du modèle, éditable par l'utilisateur
 * - Textarea autoextensible (pas d'ascenseur)
 * - Utilise Dialog.Root Radix (CAS 2) : ne ferme jamais le Sheet parent
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Mail, X, User, AlertCircle, Loader2, ChevronDown, ChevronUp, Plus, Send, Eye } from 'lucide-react';
import API, { getPersonnesEntreprise } from '../api';

// ── Reproduit _email_wrapper de email_service.py ─────────────────────────────
function buildEmailHtml(sujet, corps, logoUrl) {
  const bodyHtml = (corps || '').replace(/\n/g, '<br/>');
  const isCustomLogo = !!logoUrl;
  const headerBg = isCustomLogo
    ? 'background:white;'
    : 'background:linear-gradient(135deg,#1A3A6E 0%,#2563EB 100%);';
  const logoTag = isCustomLogo
    ? `<img src="${logoUrl}" alt="Logo" width="120" height="auto" style="display:block;max-height:48px;width:auto;border:0;" />`
    : '';
  const brandName = isCustomLogo
    ? ''
    : '<span style="color:white;font-size:20px;font-weight:700;letter-spacing:-0.5px;">Bedee</span>';

  return `<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#F1F5F9;font-family:'Helvetica Neue',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F1F5F9;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">
        <tr>
          <td style="${headerBg}padding:24px 32px;border-radius:12px 12px 0 0;text-align:left;">
            <table cellpadding="0" cellspacing="0">
              <tr>
                <td style="padding-right:12px;vertical-align:middle;">${logoTag}</td>
                <td style="vertical-align:middle;">${brandName}</td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="background:white;padding:36px 32px;border-radius:0 0 12px 12px;">
            <h1 style="margin:0 0 16px;color:#0F172A;font-size:22px;font-weight:700;">${sujet || ''}</h1>
            <div style="color:#334155;font-size:15px;line-height:1.8;">${bodyHtml}</div>
          </td>
        </tr>
        <tr>
          <td style="padding:20px 32px;text-align:center;">
            <p style="margin:0;color:#94A3B8;font-size:12px;">
              Bedee · Gestion de parc matériel<br>
              Cet email a été envoyé automatiquement, merci de ne pas y répondre.
            </p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function applyVars(text, vars = {}) {
  if (!text) return '';
  return Object.entries(vars).reduce(
    (t, [k, v]) => t.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v ?? ''),
    text
  );
}

export default function EmailComposeModal({
  open,
  onClose,
  onSend,
  destinataires = [],
  loadingDestinataires = false,
  typeCode = null,
  templateVariables = {},
  agenceId = null,
  entrepriseId = null,   // pour le sélecteur de contacts
  titre = 'Envoyer par email',
  documentRef = '',
  infoNote = '',
  sending = false,
  creatable = true,      // false = sélection uniquement (mode signature)
}) {
  // ── Recipients combobox state ──────────────────────────────────────────────
  const [chips, setChips]             = useState([]);
  const [comboInput, setComboInput]   = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const comboInputRef = useRef(null);
  // ── Contenu email ──────────────────────────────────────────────────────────
  const [sujet, setSujet]         = useState('');
  const [corps, setCorps]         = useState('');
  const [rawSujet, setRawSujet]   = useState('');
  const [rawCorps, setRawCorps]   = useState('');
  const [userEdited, setUserEdited] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingTmpl, setLoadingTmpl] = useState(false);
  // Contacts entreprise pour le dropdown
  const [entrepriseContacts, setEntrepriseContacts] = useState([]);
  const iframeRef  = useRef(null);
  const corpsRef   = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const logoUrl = agenceId ? `${backendUrl}/api/public/agence-logo/${agenceId}` : null;

  // Auto-resize du textarea corps
  const autoResize = useCallback(() => {
    const el = corpsRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);

  // Pré-remplir les chips depuis destinataires à l'ouverture
  useEffect(() => {
    if (!open) return;
    setChips(destinataires.map(d => ({ email: d.email, nom: d.nom || d.email, personne_id: d.personne_id || null })));
  }, [destinataires, open]);

  // Charger le modèle email : stocker la version brute (avant substitution)
  useEffect(() => {
    if (!open || !typeCode) return;
    setLoadingTmpl(true);
    setUserEdited(false);
    API.get(`/api/email-templates/${typeCode}`)
      .then(r => {
        const tmpl = r.data;
        setRawSujet(tmpl.sujet || tmpl.sujet_defaut || '');
        setRawCorps(tmpl.corps_html || tmpl.corps_defaut || '');
      })
      .catch(() => {})
      .finally(() => setLoadingTmpl(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, typeCode]);

  // Appliquer les variables dès que le template brut OU les variables changent
  // (couvre le cas où factureMeta arrive après l'ouverture de la modale)
  const _varsKey = JSON.stringify(templateVariables);
  useEffect(() => {
    if (!open || (!rawSujet && !rawCorps)) return;
    if (userEdited) return;
    setSujet(applyVars(rawSujet, templateVariables));
    setCorps(applyVars(rawCorps, templateVariables));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSujet, rawCorps, _varsKey, open]);

  // Auto-resize après chargement du corps
  useEffect(() => {
    if (corps) setTimeout(autoResize, 50);
  }, [corps, autoResize]);

  // Reset à la fermeture
  useEffect(() => {
    if (!open) {
      setChips([]);
      setComboInput('');
      setDropdownOpen(false);
      setHighlighted(-1);
      setCorps('');
      setSujet('');
      setRawSujet('');
      setRawCorps('');
      setUserEdited(false);
      setShowPreview(false);
    }
  }, [open]);

  // Charger les contacts de l'entreprise quand la modale s'ouvre
  useEffect(() => {
    if (!open || !entrepriseId) { setEntrepriseContacts([]); return; }
    getPersonnesEntreprise(entrepriseId)
      .then(r => setEntrepriseContacts(r.data?.data || r.data || []))
      .catch(() => setEntrepriseContacts([]));
  }, [open, entrepriseId]);

  // Injecter le HTML dans l'iframe quand l'aperçu devient visible
  useEffect(() => {
    if (!showPreview || !iframeRef.current) return;
    const html = buildEmailHtml(sujet, corps, logoUrl);
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    const resize = () => {
      try {
        const h = doc.body?.scrollHeight;
        if (h) iframeRef.current.style.height = h + 'px';
      } catch (_) {}
    };
    iframeRef.current.onload = resize;
    setTimeout(resize, 150);
  }, [showPreview, sujet, corps, logoUrl]);

  // ── Pool de contacts pour le dropdown (destinataires + entreprise) ───────────
  const allContacts = useMemo(() => {
    const pool = {};
    destinataires.forEach(d => { if (d.email) pool[d.email] = { email: d.email, nom: d.nom || d.email, personne_id: d.personne_id || null }; });
    entrepriseContacts.forEach(c => {
      const email = c.email || c.email_pro || '';
      if (!email) return;
      const nom = [c.prenom, c.nom].filter(Boolean).join(' ') || email;
      if (!pool[email]) pool[email] = { email, nom, personne_id: c.personne_id || null };
    });
    return Object.values(pool);
  }, [destinataires, entrepriseContacts]);

  const filteredContacts = allContacts.filter(c => {
    if (chips.some(ch => ch.email === c.email)) return false;
    if (!comboInput) return true;
    return (c.nom + ' ' + c.email).toLowerCase().includes(comboInput.toLowerCase());
  });

  const canAddTyped = creatable && comboInput.includes('@') && comboInput.includes('.')
    && !chips.some(c => c.email === comboInput)
    && !filteredContacts.some(c => c.email === comboInput);

  const allEmails = [...new Set(chips.map(c => c.email).filter(Boolean))];

  const addChip = (chip) => {
    setChips(prev => prev.some(c => c.email === chip.email) ? prev : [...prev, chip]);
    setComboInput('');
    setDropdownOpen(false);
    setHighlighted(-1);
    setTimeout(() => comboInputRef.current?.focus(), 0);
  };

  const removeChip = (email) => setChips(prev => prev.filter(c => c.email !== email));

  const handleComboKeyDown = (e) => {
    const options = [...filteredContacts, ...(canAddTyped ? [{ email: comboInput, nom: comboInput }] : [])];
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, options.length - 1)); setDropdownOpen(true); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && options[highlighted]) addChip(options[highlighted]);
      else if (canAddTyped) addChip({ email: comboInput, nom: comboInput });
    }
    else if (e.key === 'Backspace' && !comboInput && chips.length > 0) removeChip(chips[chips.length - 1].email);
    else if (e.key === 'Escape') setDropdownOpen(false);
    else if ((e.key === ',' || e.key === ' ') && creatable && comboInput.includes('@')) { e.preventDefault(); addChip({ email: comboInput, nom: comboInput }); }
  };

  const handleSend = () => {
    if (!allEmails.length || sending) return;
    onSend({ emails: allEmails, recipients: chips, sujet, corps });
  };

  const fieldStyle = {
    width: '100%', padding: '9px 12px', fontSize: '13px',
    border: '1px solid #E2E8F0', borderRadius: '8px',
    outline: 'none', color: '#0F172A', boxSizing: 'border-box',
    transition: 'border-color 0.15s', fontFamily: 'inherit',
  };
  const focusIn  = e => { e.target.style.borderColor = '#93C5FD'; };
  const focusOut = e => { e.target.style.borderColor = '#E2E8F0'; };

  return (
    <Dialog.Root open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay style={{
          position: 'fixed', inset: 0, zIndex: 10500,
          background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)',
        }} />

        <Dialog.Content
          data-testid="email-compose-modal"
          onInteractOutside={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
          style={{
            position: 'fixed',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10501,
            width: 'min(96vw, 640px)',
            maxHeight: '90vh',
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.22)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            outline: 'none',
          }}
        >
          {/* ── En-tête ── */}
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Mail size={16} color="#1D4ED8" />
              </div>
              <div>
                <p style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', margin: 0 }}>{titre}</p>
                {documentRef && <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>{documentRef}</p>}
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                data-testid="email-modal-close"
                onClick={onClose}
                style={{ padding: 6, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 8, color: '#94A3B8' }}
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          {/* ── Corps scrollable ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Destinataires — combobox + chips */}
            <section>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>
                Destinataires
              </p>
              {loadingDestinataires ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94A3B8', fontSize: 13, padding: '10px 0' }}>
                  <Loader2 size={14} className="animate-spin" /> Chargement…
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Zone chips + input */}
                  <div
                    style={{
                      border: `1px solid ${dropdownOpen ? '#93C5FD' : '#E2E8F0'}`,
                      borderRadius: 10, padding: '6px 10px',
                      display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                      cursor: 'text', minHeight: 44, transition: 'border-color 0.15s',
                      background: '#fff',
                    }}
                    onClick={() => { comboInputRef.current?.focus(); setDropdownOpen(true); }}
                  >
                    {chips.map(chip => (
                      <span key={chip.email} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: '#DBEAFE', color: '#1D4ED8',
                        fontSize: 12, fontWeight: 600, padding: '3px 6px 3px 10px',
                        borderRadius: 999, flexShrink: 0, lineHeight: 1.4,
                      }}>
                        {chip.nom && chip.nom !== chip.email ? chip.nom : chip.email}
                        <button
                          type="button"
                          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); removeChip(chip.email); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center', color: '#1D4ED8', opacity: 0.7, lineHeight: 1 }}
                          data-testid={`email-chip-remove-${chip.email}`}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                    <input
                      ref={comboInputRef}
                      type="text"
                      value={comboInput}
                      onChange={e => { setComboInput(e.target.value); setDropdownOpen(true); setHighlighted(-1); }}
                      onFocus={() => setDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setDropdownOpen(false), 160)}
                      onKeyDown={handleComboKeyDown}
                      placeholder={chips.length === 0 ? (creatable ? 'Rechercher ou saisir un email…' : 'Rechercher un contact…') : ''}
                      style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', fontSize: 13, background: 'transparent', padding: '3px 0' }}
                      data-testid="email-modal-recipient-input"
                    />
                  </div>

                  {/* Dropdown */}
                  {dropdownOpen && (filteredContacts.length > 0 || canAddTyped) && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                      border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff',
                      boxShadow: '0 8px 28px rgba(0,0,0,0.13)', zIndex: 100, maxHeight: 200, overflowY: 'auto',
                    }}>
                      {filteredContacts.map((c, i) => (
                        <div key={c.email}
                          onMouseDown={e => { e.preventDefault(); addChip(c); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                            cursor: 'pointer', transition: 'background 0.1s',
                            background: highlighted === i ? '#EFF6FF' : 'transparent',
                            borderBottom: (i < filteredContacts.length - 1 || canAddTyped) ? '1px solid #F1F5F9' : 'none',
                          }}
                          onMouseEnter={() => setHighlighted(i)}
                          data-testid={`email-contact-option-${i}`}
                        >
                          <div style={{ width: 28, height: 28, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <User size={13} color="#1D4ED8" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nom}</p>
                            <p style={{ fontSize: 11, color: '#64748B', margin: 0 }}>{c.email}</p>
                          </div>
                        </div>
                      ))}
                      {canAddTyped && (
                        <div
                          onMouseDown={e => { e.preventDefault(); addChip({ email: comboInput, nom: comboInput }); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                            cursor: 'pointer', transition: 'background 0.1s',
                            background: highlighted === filteredContacts.length ? '#F0FDF4' : 'transparent',
                          }}
                          onMouseEnter={() => setHighlighted(filteredContacts.length)}
                          data-testid="email-add-typed"
                        >
                          <div style={{ width: 28, height: 28, borderRadius: 8, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Plus size={13} color="#166534" />
                          </div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#166534', margin: 0 }}>Ajouter «{comboInput}»</p>
                            <p style={{ fontSize: 11, color: '#64748B', margin: 0 }}>Nouvelle adresse email</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Objet */}
            <section>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>Objet</p>
              {loadingTmpl
                ? <div style={{ height: 38, background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', paddingLeft: 12 }}><Loader2 size={13} color="#94A3B8" className="animate-spin" /></div>
                : <input type="text" value={sujet} onChange={e => { setSujet(e.target.value); setUserEdited(true); }}
                    data-testid="email-modal-sujet" placeholder="Objet de l'email"
                    style={fieldStyle} onFocus={focusIn} onBlur={focusOut} />
              }
            </section>

            {/* Corps du message */}
            <section>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>Message</p>
              {loadingTmpl
                ? <div style={{ height: 120, background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', paddingLeft: 12 }}><Loader2 size={13} color="#94A3B8" className="animate-spin" /></div>
                : <textarea
                    ref={corpsRef}
                    value={corps}
                    onChange={e => { setCorps(e.target.value); setUserEdited(true); autoResize(); }}
                    data-testid="email-modal-corps"
                    placeholder="Corps du message…"
                    style={{
                      ...fieldStyle,
                      resize: 'none',
                      overflow: 'hidden',
                      minHeight: 80,
                      lineHeight: 1.7,
                    }}
                    onFocus={focusIn} onBlur={focusOut}
                  />
              }
            </section>

            {/* Aperçu (collapsible) — identique au mail envoyé */}
            {typeCode && (
              <section>
                <button
                  onClick={() => setShowPreview(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569', padding: '4px 0' }}
                >
                  {showPreview ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  <Eye size={12} style={{ marginRight: 2 }} />
                  Aperçu du mail envoyé
                </button>
                {showPreview && (
                  <div style={{ marginTop: 8, border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
                    <iframe ref={iframeRef} title="Aperçu email"
                      style={{ width: '100%', minHeight: 200, border: 'none', display: 'block' }}
                      sandbox="allow-same-origin" />
                  </div>
                )}
              </section>
            )}

            {infoNote && (
              <div style={{ background: '#EFF6FF', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1E40AF', lineHeight: 1.5 }}>
                {infoNote}
              </div>
            )}
          </div>

          {/* ── Pied de page ── */}
          <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E8F0', background: '#FAFAFA', display: 'flex', gap: 10, flexShrink: 0 }}>
            <button onClick={onClose} data-testid="email-modal-cancel"
              style={{ flex: 1, padding: '10px 16px', fontSize: 13, fontWeight: 500, border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff', color: '#475569', cursor: 'pointer' }}>
              Annuler
            </button>
            <button onClick={handleSend}
              disabled={sending || allEmails.length === 0 || loadingDestinataires}
              data-testid="email-modal-send"
              style={{
                flex: 2, padding: '10px 16px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 10,
                background: (sending || allEmails.length === 0) ? '#BFDBFE' : '#1D4ED8',
                color: '#fff', cursor: (sending || allEmails.length === 0) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              {sending
                ? <><Loader2 size={14} className="animate-spin" /> Envoi en cours…</>
                : <><Send size={14} /> {allEmails.length > 0 ? `Envoyer à ${allEmails.length} destinataire${allEmails.length > 1 ? 's' : ''}` : 'Ajouter un destinataire'}</>
              }
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
